import React, { createElement, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { cls } from '@mssgme/helpers';
import { useRestriction } from '@mssgme/hooks';

import { getProps } from './utils';
import { FieldRowProps } from './types';
import Row from './Row';
import Label from './Label';
import FieldError from './FieldError';
import FieldWarning from './FieldWarning';

const isObject = (value) => value && typeof value === 'object';

export default function FieldRow({ as: element, id, children, tag: Tag = Row, builderInput, ...props }) {
    const {
        row: { name, label, labelClassName, errorClassName, restrictRule, required, optional, ...rowProps },
        control: controlProps,
    } = getProps(props);
    const { t } = useTranslation();
    const { hasWarning, showWarning } = useRestriction(restrictRule);
    const formik = useFormikContext();

    const handleClick = (e) => {
        e.preventDefault();
        showWarning();
    };

    const { name: formName, warning, getFieldProps, getFieldMeta } = formik;
    const meta = getFieldMeta(name);

    if (!id) {
        id = `${formName || 'form'}_${name}`.replaceAll('.', '_');
    }

    const isCustomRender = typeof children === 'function';
    const field = (isCustomRender || !children) && Object.assign(getFieldProps(name), controlProps, {
        id,
        builderInput,
    });

    const makeLabel = useCallback(
        (label, props) => (
            <Label
                required={required}
                optional={optional}
                disabled={controlProps.disabled}
                htmlFor={id}
                builderInput={builderInput}
                {...(props ? cls(labelClassName, props) : { className: labelClassName })}
            >
                {label}
            </Label>
        ),
        [labelClassName, required, optional, controlProps.disabled, id, builderInput]
    );
    let control;

    if (isCustomRender) {
        control = children({
            form: formik,
            field,
            meta,
            makeLabel,
        });
    } else {
        control = children || createElement(element || 'input', field);
    }

    return (
        <Tag {...rowProps} onClick={hasWarning ? handleClick : null}>
            {label && makeLabel(label)}
            {control}
            {warning && <FieldWarning>{t(warning)}</FieldWarning>}
            {meta.error && meta.touched && !isObject(meta.error) && (
                <FieldError className={errorClassName}>{t(meta.error)}</FieldError>
            )}
        </Tag>
    );
}

FieldRow.propTypes = FieldRowProps;
